/**
 * Grafici del diario di salute, una serie per indicatore. Condiviso fra la
 * vista coach (WellnessCoachDialog) e quella atleta (WellnessDiary) così
 * entrambi vedono gli andamenti allo stesso modo.
 * Spec: docs/wellness/00-analisi-e-piano.md
 */
import { useMemo } from "react";
import {
	buildWellnessSeries,
	isChartableField,
	wellnessFieldColor,
} from "../../lib/wellness";
import { Card, CardContent } from "../ui/card";
import { useLanguage } from "../../contexts/LanguageContext";
import {
	ResponsiveContainer,
	LineChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip as RechartsTooltip,
	Line,
} from "recharts";
import {
	useChartColors,
	useChartTooltipStyle,
} from "../../lib/useChartColors";
import { useTheme } from "../../contexts/ThemeContext";

export function WellnessCharts({
	entries = [],
	catalog = [],
	fields = [],
	emptyLabel,
}) {
	const chartColors = useChartColors();
	const tooltipStyle = useChartTooltipStyle();
	const { theme: themeName } = useTheme();
	const { t } = useLanguage();
	const chartFields = useMemo(() => {
		const selected = new Set(fields);
		return (catalog || []).filter(
			(field) => isChartableField(field) && selected.has(field.key),
		);
	}, [catalog, fields]);

	const renderable = chartFields
		.map((field) => ({
			field,
			series: buildWellnessSeries(entries, field.key),
		}))
		.filter((item) => item.series.length > 0);

	if (renderable.length === 0) {
		return (
			<p className="text-sm text-muted-foreground py-6 text-center">
				{emptyLabel || t("wellnessNoData")}
			</p>
		);
	}

	return (
		<div className="space-y-4">
			{renderable.map(({ field, series }) => {
				const color = wellnessFieldColor(field, themeName);
				return (
					<Card key={field.key} className="bg-card border-border rounded-sm">
						<CardContent className="p-3">
							<div className="flex items-center justify-between mb-2">
								<p className="text-sm font-medium">{field.label}</p>
								{field.unit && (
									<span className="text-[10px] text-muted-foreground">
										{field.unit}
									</span>
								)}
							</div>
							<div className="h-[160px]">
								<ResponsiveContainer width="100%" height="100%">
									<LineChart data={series}>
										<CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
										<XAxis
											dataKey="date"
											stroke={chartColors.axis}
											tick={{ fill: chartColors.axis, fontSize: 10 }}
										/>
										<YAxis
											stroke={chartColors.axis}
											tick={{ fill: chartColors.axis, fontSize: 10 }}
											width={32}
											domain={
												field.type === "scale_1_5" ? [0, 5] : ["auto", "auto"]
											}
										/>
										<RechartsTooltip
											contentStyle={tooltipStyle}
										/>
										<Line
											type="monotone"
											dataKey="value"
											stroke={color}
											strokeWidth={2}
											dot={{ fill: color, r: 3 }}
										/>
									</LineChart>
								</ResponsiveContainer>
							</div>
						</CardContent>
					</Card>
				);
			})}
		</div>
	);
}
